import { Alert, AlertContent, AlertText } from "@hortis/ui/alert";
import { Button, IconButton } from "@hortis/ui/button";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import {
  AlertCircle,
  FilterLines,
  Map1,
  Plus,
  SearchMd,
  Settings4,
  XClose,
} from "@hortis/ui/icons";
import { Input, InputSlot } from "@hortis/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@hortis/ui/popover";
import { CircularProgress } from "@hortis/ui/progress";
import { Sheet } from "@hortis/ui/sheet";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { centerOfMass } from "@turf/turf";
import {
  type LocationListFieldsFragment,
  type LocationOrderByInput,
} from "generated/graphql";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateHeader,
  EmptyStateTitle,
} from "src/components/empty-state";
import { FeaturedIcon } from "src/components/featured-icon/featured-icon";
import { ListFooter } from "src/components/list/list-footer";

import { parseLocationGeometry } from "src/components/map/location-bed-polygons/parse-geometry";
import { useMaplibre } from "src/components/map/utils";
import { useTopbarStore } from "src/components/nav-layout/topbar/topbar-store";
import { Skeleton } from "src/components/skeleton";
import { AddLocationFilter } from "src/features/filters/locations/add-location-filter";
import { LocationFilters } from "src/features/filters/locations/filters";
import { LocationFilterModals } from "src/features/filters/locations/modals/filter-modals";
import { LocationSortSelect } from "src/features/filters/locations/sort-select";
import type { LocationFilterType } from "src/features/filters/locations/types";
import type { LocationFilters as LocationFiltersType } from "src/features/filters/locations/use-location-filters";
import { NewLocationSheetContent } from "src/features/locations/components/location-drawer";
import { getLocationsDocument } from "src/features/locations/graphql";
import { useAccessRole } from "src/features/permissions/use-access-role";
import { LocationCard } from "src/features/records/components/new-record-card/variants/location-card";
import { useControlledDebounce } from "src/utils/hooks/debounce";
import { useGqlQuery } from "src/utils/hooks/graphql";
import { useIsScrolled } from "src/utils/hooks/scrolled";
import { PageDirection } from "src/utils/pagination";
import { twMerge } from "tailwind-merge";
import { bbox, mapId } from "../../utils";
import { useHoveredListLocationStore } from "../hovered-list-location-store";
import { useMapOptionsStore } from "../map-options-store";

const ErrorAlert = () => (
  <Alert color="error" data-cy="error-alert" className="mx-3 my-2">
    <AlertCircle />
    <AlertContent>
      <AlertText>Something went wrong</AlertText>
    </AlertContent>
  </Alert>
);

const ListItem = ({
  location,
  onClick,
}: {
  location: LocationListFieldsFragment & {
    geometry?: string;
    geometryColor?: string | null;
  };
  onClick: (row: LocationListFieldsFragment) => void;
}) => {
  const isNotMobile = useMediaQuery("sm");
  const { setHoveredListLocation } = useHoveredListLocationStore();
  const { setLocationPolygonsEnabled } = useMapOptionsStore();
  const handleMouseEnter = useCallback(() => {
    setHoveredListLocation({
      id: location.id,
      name: location.name,
      color: location.geometryColor,
    });
  }, [
    location.geometryColor,
    location.id,
    location.name,
    setHoveredListLocation,
  ]);
  const handleMouseLeave = useCallback(() => {
    setHoveredListLocation(undefined);
  }, [setHoveredListLocation]);

  return (
    <Link
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      from="/site/$siteSlug/map"
      to="/site/$siteSlug/map/location/$locationName"
      onClick={() => {
        setLocationPolygonsEnabled(true);
        onClick(location);
      }}
      params={{
        locationName: location.name,
      }}
      className={twMerge(
        "group flex w-full cursor-pointer rounded-xl border-none border-grey-200 px-3! py-3 text-left hover:bg-grey-50 hover:transition-none  sm:px-6 not-last:border-b",
      )}
      data-cy="map-list-location-row"
    >
      <LocationCard
        hasGeometry={location.geometry != null}
        name={location.name}
        description={location.description}
        className={twMerge("w-full p-0 transition-none group-hover:bg-grey-50")}
        variant="Row"
        size={isNotMobile ? "md" : "sm"}
      />
    </Link>
  );
};

interface MapLocationListProps {
  cursor: string | null;
  setCursor: (val: string) => void;
  locationFilters: LocationFiltersType;
  searchTerm: string;
  setSearchTerm: (s: string) => void;
  orderBy: LocationOrderByInput | undefined;
  setOrderBy: (val: LocationOrderByInput | undefined) => void;
  newLocationDrawerOpen: boolean;
  setNewLocationDrawerOpen: (val: boolean) => void;
}

const routeApi = getRouteApi("/_layout/site/$siteSlug/map");

export const LocationListSkeleton = () => (
  <div className="flex w-full gap-3 p-3 md:gap-4">
    <div className="flex flex-col">
      <Skeleton height="32px" width="172px" animation="wave" />
      <Skeleton height="24px" width="190px" animation="wave" />
    </div>
  </div>
);

export const MapLocationList = ({
  cursor,
  setCursor,
  locationFilters: filters,
  searchTerm,
  setSearchTerm,
  orderBy,
  newLocationDrawerOpen,
  setNewLocationDrawerOpen,
  setOrderBy, // eslint-disable-next-line sonarjs/cognitive-complexity
}: MapLocationListProps) => {
  const { siteSlug } = routeApi.useParams();
  const { subdomain } = routeApi.useRouteContext();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const searchTermDebouncer = useControlledDebounce(setDebouncedSearchTerm);
  const navigate = useNavigate();
  const { canEdit } = useAccessRole();
  const { [mapId]: map } = useMaplibre();
  const [dir, setDir] = useState(PageDirection.Next);
  const { attachRef, ref: containerRef } = useIsScrolled();
  const [loadingDirection, setLoadingDirection] =
    useState<PageDirection | null>(null);

  const handleSearchTermChange = (val: string) => {
    setSearchTerm(val);
    searchTermDebouncer.schedule(val);
  };

  const rowsPerPage = 25;
  const {
    data,
    error,
    isFetching,
    isPending,
    refetch: refetchLocations,
  } = useGqlQuery({
    query: getLocationsDocument,
    variables: {
      searchTerm: debouncedSearchTerm,
      filter: filters.preparedFilters,
      orderBy,
      siteSlug,
      subdomain,
      before: dir === PageDirection.Prev ? cursor : undefined,
      after: dir === PageDirection.Next ? cursor : undefined,
      last: dir === PageDirection.Prev ? rowsPerPage : undefined,
      first: dir === PageDirection.Next ? rowsPerPage : undefined,
    },
  });
  const fetching = isFetching || isPending;

  const pageInfo = data?.org?.site?.locationsConnection?.pageInfo;
  const locations = data?.org?.site?.locationsConnection?.nodes;
  const total = data?.org?.site?.locationsTotal;

  const onPrevPage = () => {
    if (pageInfo?.startCursor != null && pageInfo.hasPreviousPage) {
      setCursor(pageInfo.startCursor);
      setDir(PageDirection.Prev);
    }
  };

  const onNextPage = () => {
    if (pageInfo?.endCursor != null && pageInfo.hasNextPage) {
      setCursor(pageInfo.endCursor);
      setDir(PageDirection.Next);
    }
  };

  const clearSearch = () => {
    if (searchTerm.length > 0) {
      handleSearchTermChange("");
    }
  };

  useEffect(() => {
    if (!fetching) {
      if (containerRef != null) {
        containerRef.scrollTop = 0;
      }
      setLoadingDirection(null);
    }
  }, [fetching, containerRef]);

  const [openFilter, setOpenFilter] = useState<LocationFilterType | false>(
    false,
  );

  const searchActive = searchTerm.length > 0 && searchTerm !== "";
  const filterButtonRef = useRef<HTMLButtonElement>(null);

  const setTitle = useTopbarStore((state) => state.setTitle);

  const handleRowClick = useCallback(
    (location: LocationListFieldsFragment & { geometry?: string }) => {
      if (location.geometry == null) {
        return;
      }
      const parsedLocationGeometry = parseLocationGeometry(location.geometry);
      if (parsedLocationGeometry == null) {
        return;
      }
      const { geometry } = centerOfMass(parsedLocationGeometry);

      if (geometry.coordinates[0] == null || geometry.coordinates[1] == null) {
        return;
      }

      map?.fitBounds(bbox(parsedLocationGeometry), {
        padding: 30,
      });
    },
    [map],
  );

  const pageTitle = "Map";
  setTitle(pageTitle);

  return (
    <>
      <div className="flex justify-between gap-2 border-b border-grey-200 px-4 py-4 lg:px-6">
        <Input
          placeholder="Search locations"
          className={twMerge(
            "w-full rounded-lg text-base [&_input]:w-px [&_input]:max-w-full [&_input]:py-1",
          )}
          value={searchTerm}
          onChange={(e) => {
            handleSearchTermChange(e.target.value);
          }}
          data-cy="location-search-input"
        >
          <InputSlot side="right" className="flex gap-3">
            {fetching ? (
              <CircularProgress size={18} className="mr-2 text-primary-500" />
            ) : (
              searchTerm !== "" && (
                <IconButton
                  icon={XClose}
                  variant="tertiaryGray"
                  size="sm"
                  ariaLabel="Clear"
                  className="p-1!"
                  onClick={clearSearch}
                  data-cy="location-search-input-clear"
                />
              )
            )}
            <div className="-mx-[3px]">
              <SearchMd size="20px" className="text-grey-500 " />
            </div>
          </InputSlot>
        </Input>

        <AddLocationFilter
          setOpenFilter={(type) => {
            setOpenFilter(type.filterType);
          }}
          align="center"
          iconButton
          buttonProps={{
            size: "sm",
            testId: "add-filter-button",
            icon: FilterLines,
            ref: filterButtonRef,
            className: "border",
            variant: "secondaryGray",
          }}
        />
        <LocationFilterModals
          openFilter={openFilter}
          handleFilterModalClose={() => {
            setOpenFilter(false);
          }}
          filterModalAnchorEl={filterButtonRef}
          dispatchFilters={filters.dispatchFilters}
        />

        <Popover>
          <PopoverTrigger asChild>
            <IconButton
              icon={Settings4}
              ariaLabel="View settings"
              size="sm"
              testId="view-settings-button"
            />
          </PopoverTrigger>
          <PopoverContent className="min-w-44">
            <div className="border-b border-grey-200 px-3.5 py-4">
              <LocationSortSelect
                fullWidth
                buttonProps={{ size: "xs" }}
                orderBy={orderBy}
                updateOrderBy={setOrderBy}
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {filters.filterCounts.total > 0 && (
        <div className="border-b border-grey-200 px-4 py-4 lg:px-6">
          <LocationFilters
            filters={filters.filters}
            dispatchFilters={filters.dispatchFilters}
            resetFilters={filters.resetFilters}
            filterCount={filters.filterCounts.total}
            hideClear
          />
        </div>
      )}

      <div className="flex-1 overflow-y-auto" ref={attachRef}>
        <div className="flex flex-col gap-0.5 px-1 py-1 lg:px-3">
          {((locations == null && !fetching) || error != null) && (
            <ErrorAlert />
          )}
          {locations == null && fetching && (
            <>
              <LocationListSkeleton />
              <LocationListSkeleton />
              <LocationListSkeleton />
              <LocationListSkeleton />
              <LocationListSkeleton />
              <LocationListSkeleton />
              <LocationListSkeleton />
              <LocationListSkeleton />
              <LocationListSkeleton />
              <LocationListSkeleton />
            </>
          )}
          {locations?.map((location) => (
            <ListItem
              key={location.id}
              location={location}
              onClick={handleRowClick}
            />
          ))}
        </div>
        {locations != null && locations.length === 0 && (
          <EmptyState className="my-12 justify-self-center p-6 md:my-24">
            <EmptyStateHeader>
              <FeaturedIcon>
                {searchActive ? (
                  <SearchMd className="h-5 w-5 text-primary-600" />
                ) : (
                  <Map1 className="h-5 w-5 text-primary-600" />
                )}
              </FeaturedIcon>
              <EmptyStateTitle>No locations found</EmptyStateTitle>
              <EmptyStateBody>
                {searchTerm.length > 0 &&
                  searchTerm !== "" &&
                  `Your search “${searchTerm}” did not match any records. Please try again${
                    canEdit ? " or create location" : ""
                  }.`}
              </EmptyStateBody>
            </EmptyStateHeader>
            <EmptyStateActions>
              {searchActive && (
                <Button testId="clear-search-button" onClick={clearSearch}>
                  Clear search
                </Button>
              )}
              {canEdit && (
                <div className="shrink-0 md:flex">
                  <Button
                    testId="open-create-location-drawer"
                    onClick={() => {
                      setNewLocationDrawerOpen(true);
                    }}
                    fullWidth
                    startIcon={Plus}
                    variant="primary"
                  >
                    New location
                  </Button>
                </div>
              )}
            </EmptyStateActions>
          </EmptyState>
        )}
      </div>
      <Sheet
        open={newLocationDrawerOpen}
        onOpenChange={setNewLocationDrawerOpen}
      >
        <NewLocationSheetContent
          onClose={() => {
            setNewLocationDrawerOpen(false);
          }}
          open={newLocationDrawerOpen}
          onLocationCreation={(location) => {
            void navigate({
              to: "/site/$siteSlug/map/location/$locationName",
              from: "/site/$siteSlug/map",
              params: {
                locationName: location.name,
              },
            });
            setNewLocationDrawerOpen(false);
            void refetchLocations();
          }}
        />
      </Sheet>
      <ListFooter
        total={total}
        onPreviousPage={onPrevPage}
        onNextPage={onNextPage}
        hasPreviousPage={pageInfo?.hasPreviousPage}
        hasNextPage={pageInfo?.hasNextPage}
        previousPageLoading={loadingDirection === PageDirection.Prev}
        nextPageLoading={loadingDirection === PageDirection.Next}
        testIdPrefix="locations"
      />
    </>
  );
};
